// src/utils/voiceStore.js
// Voice notes are written to IndexedDB chunk-by-chunk while recording, so a
// failed transcription, a closed tab or a dropped connection never loses the
// audio. A recording is deleted only after it transcribes (or the user discards
// it). Every call degrades to a no-op when IndexedDB is unavailable (private
// windows); the hook then keeps the failed blob in memory instead.

const DB_NAME = "cbc-voice-notes";
const RECS = "recs";
const CHUNKS = "chunks";
export const VOICE_CHANGED_EVENT = "cbc:voice-recs-changed";

let dbPromise = null;
function openDb() {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise((resolve, reject) => {
    if (typeof indexedDB === "undefined") return reject(new Error("IndexedDB unavailable"));
    const req = indexedDB.open(DB_NAME, 1);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(RECS)) db.createObjectStore(RECS, { keyPath: "id" });
      if (!db.objectStoreNames.contains(CHUNKS)) {
        db.createObjectStore(CHUNKS, { autoIncrement: true }).createIndex("recId", "recId");
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  }).catch((e) => { dbPromise = null; throw e; });
  return dbPromise;
}

function done(tx) {
  return new Promise((resolve, reject) => {
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
    tx.onabort = () => reject(tx.error);
  });
}

function notify() {
  try { window.dispatchEvent(new CustomEvent(VOICE_CHANGED_EVENT)); } catch {}
}

export async function putRec(rec) {
  try {
    const db = await openDb();
    const tx = db.transaction(RECS, "readwrite");
    tx.objectStore(RECS).put(rec);
    await done(tx);
    return true;
  } catch {
    return false;
  }
}

export async function addChunk(recId, data) {
  try {
    const db = await openDb();
    const tx = db.transaction(CHUNKS, "readwrite");
    tx.objectStore(CHUNKS).add({ recId, data });
    await done(tx);
    return true;
  } catch {
    return false;
  }
}

export async function listRecs() {
  try {
    const db = await openDb();
    const tx = db.transaction(RECS, "readonly");
    const req = tx.objectStore(RECS).getAll();
    await done(tx);
    return (req.result || []).sort((a, b) => (a.startedAt || 0) - (b.startedAt || 0));
  } catch {
    return [];
  }
}

export async function getRecBlob(rec) {
  try {
    const db = await openDb();
    const tx = db.transaction(CHUNKS, "readonly");
    const req = tx.objectStore(CHUNKS).index("recId").getAll(rec.id);
    await done(tx);
    const parts = (req.result || []).map((c) => c.data);
    return parts.length ? new Blob(parts, { type: rec.mimeType || "audio/webm" }) : null;
  } catch {
    return null;
  }
}

export async function deleteRec(id) {
  try {
    const db = await openDb();
    const tx = db.transaction([RECS, CHUNKS], "readwrite");
    tx.objectStore(RECS).delete(id);
    const idx = tx.objectStore(CHUNKS).index("recId");
    const cur = idx.openKeyCursor(IDBKeyRange.only(id));
    cur.onsuccess = () => {
      const c = cur.result;
      if (!c) return;
      tx.objectStore(CHUNKS).delete(c.primaryKey);
      c.continue();
    };
    await done(tx);
  } catch {}
  notify();
}

export { notify as notifyVoiceRecsChanged };

// "recording.webm" / "recording.mp4" — the server hands this extension to
// Whisper, which picks the decoder from it.
export function audioExt(mimeType) {
  const m = String(mimeType || "").toLowerCase();
  if (m.includes("mp4") || m.includes("m4a") || m.includes("aac")) return "mp4";
  if (m.includes("ogg")) return "ogg";
  if (m.includes("wav")) return "wav";
  if (m.includes("mpeg") || m.includes("mp3")) return "mp3";
  return "webm";
}
